import { themeAtom } from "../../shared/atoms";
import { Bq5CargoArrangementsM } from "../models/bq5-cargo-arrangements.model";
import { InputCell } from "../services/baltic-forms.utils";
import { useAtomValue } from "jotai";
import React from "react";
import { twMerge } from "tailwind-merge";

export function Bq5DeckAndHatches(
  form: Bq5CargoArrangementsM,
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void,
  handleHatchDimensionChange: (index: number, field: "dimensionl" | "dimensionw", value: string) => void,
  handleHatchCoverStrengthChange: (index: number, value: string) => void,
  handleCementFeederHolesChange: (index: number, field: "numberDiameter" | "location", value: string) => void,
  toggleComments: (inputId: string) => void,
  isCommentsVisible: (inputId: string) => boolean,
  readonly: boolean,
) {
  const theme = useAtomValue(themeAtom);

  return (
    <div>
      <h2 className="mb-2 mt-[120px] text-lg font-semibold">Deck and Hatches</h2>

      <div className="mt-4 grid grid-cols-4 gap-4">
        {/* Number of hatches */}
        <div className="col-span-1">
          <label className="block text-sm font-bold">Number of hatches</label>
          <input
            disabled={readonly}
            type="number"
            name="numberOfHatches"
            value={form.numberOfHatches}
            onChange={handleChange}
            className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
          />
        </div>

        {/* Make and type */}
        <div className="col-span-1">
          <label className="block text-sm font-bold">Make and type of hatch covers</label>
          <textarea
            disabled={readonly}
            name="hatchCoverMakeType"
            value={form.hatchCoverMakeType}
            onChange={handleChange}
            rows={3}
            className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
          />
        </div>
      </div>

      {/* Hatch dimensions */}
      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Hatch dimensions (L x W)</label>
          {form.hatchDimensions?.map((hd, idx) => (
            <div key={hd.hatchNumber} className="my-1 flex items-center space-x-2">
              <label className="w-24 text-xs font-bold">Hatch #{hd.hatchNumber}</label>
              <InputCell
                disabled={readonly}
                name="dimensionl"
                value={hd.dimensionl}
                onChange={(e) => handleHatchDimensionChange(idx, "dimensionl", e.target.value)}
                trailingText="L"
                inputType="number"
              />
              <InputCell
                disabled={readonly}
                name="dimensionw"
                value={hd.dimensionw}
                onChange={(e) => handleHatchDimensionChange(idx, "dimensionw", e.target.value)}
                trailingText="W"
                inputType="number"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Hatch span (distance front to rear) */}
      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">
            Hatch span (distance from front of forward hatch to aft of rear hatch):
          </label>
          <InputCell
            disabled={readonly}
            name="hatchSpanDistance"
            value={form.hatchSpanDistance}
            onChange={handleChange}
            trailingText="Meters"
            inputType="number"
          />
        </div>

        {/* Hatch cover strengths */}
        <div className="col-span-1">
          <label className="block text-sm font-bold">Hatch cover strengths</label>
          {form.hatchCoverStrengths?.map((hs, idx) => (
            <div key={hs.hatchNumber} className="my-1 flex items-center space-x-2">
              <label className="w-20 text-xs font-bold">Cover #{hs.hatchNumber}</label>
              <InputCell
                disabled={readonly}
                name="strength"
                value={hs.strength}
                onChange={(e) => handleHatchCoverStrengthChange(idx, e.target.value)}
                trailingText="MT/sqm"
                inputType="number"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Cement feeder holes in hatch covers */}
      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-2">
          <div className="flex flex-row">
            <label className="block flex-1 text-sm font-bold">
              Number and diameter of cement feeder holes in hatch cover
            </label>
            <label className="block flex-1 text-sm font-bold">Location of cement feeder holes in hatch cover</label>
          </div>
          {form.cementFeederHoles?.map((cf, idx) => (
            <div key={cf.hatchNumber} className="my-1 flex flex-col sm:flex-row sm:space-x-4">
              <div className="flex w-full items-center space-x-2 sm:w-1/2">
                <label className="w-20 text-xs font-bold">Hatch #{cf.hatchNumber}</label>
                <input
                  disabled={readonly}
                  type="text"
                  placeholder="Number & Diameter"
                  value={cf.numberDiameter}
                  onChange={(e) => handleCementFeederHolesChange(idx, "numberDiameter", e.target.value)}
                  className={twMerge("w-full rounded-md border p-2 focus:outline-none focus:ring")}
                />
              </div>
              <div className="mt-2 flex w-full items-center space-x-2 sm:mt-0 sm:w-1/2">
                <label className="whitespace-nowrap text-xs font-bold">Location:</label>
                <input
                  disabled={readonly}
                  type="text"
                  placeholder="Location in cover"
                  value={cf.location}
                  onChange={(e) => handleCementFeederHolesChange(idx, "location", e.target.value)}
                  className={twMerge("w-full rounded-md border p-2 focus:outline-none focus:ring")}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Distance from ship’s rail to near/far edge of hatch covers */}
      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-2">
          <label className="block text-sm font-bold">
            Distance from ship's rail to near and far edge of hatch covers/coaming near and far (Please advise the
            minimum width clear of any obstruction for each hold):
          </label>
          <textarea
            disabled={readonly}
            name="distanceNearFarEdge"
            value={form.distanceNearFarEdge}
            onChange={handleChange}
            rows={3}
            className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
          />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Distance bow to fore of 1st hold</label>
          <InputCell
            disabled={readonly}
            name="distanceBowFore"
            value={form.distanceBowFore}
            onChange={handleChange}
            trailingText="Meters"
            inputType="number"
          />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Distance stern to aft of last hold</label>
          <InputCell
            disabled={readonly}
            name="distanceSternAft"
            value={form.distanceSternAft}
            onChange={handleChange}
            trailingText="Meters"
            inputType="number"
          />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Deck strength</label>
          <InputCell
            disabled={readonly}
            name="deckStrength"
            value={form.deckStrength}
            onChange={handleChange}
            trailingText="MT/sqm"
            inputType="number"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("deckStrengthComments")}
            >
              Comments
            </button>
          </label>
          {(form.deckStrengthComments || isCommentsVisible("deckStrengthComments")) && (
            <textarea
              disabled={readonly}
              name="deckStrengthComments"
              value={form.deckStrengthComments}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>
    </div>
  );
}
